import { Button } from "@universe/ui/components/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@universe/ui/components/tooltip";
import { cn } from "@universe/ui/lib/utils";
import { motion } from "framer-motion";
import { LucideIcon } from "lucide-react";
import { ReactNode, useId } from "react";

import { AppLink } from "@/components/app-link";

interface ActionIconButtonProps {
  icon: LucideIcon;
  label: ReactNode;
  labelClassName?: string;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  tooltipText?: string;
  variant?: "default" | "outline" | "secondary" | "ghost";
  className?: string;
  iconClassName?: string;
  trigger?: ReactNode;
  strokeWidth?: number;
}

export default function ActionIconButton({
  icon: Icon,
  label,
  labelClassName,
  href,
  onClick,
  disabled = false,
  tooltipText,
  variant = "secondary",
  className,
  iconClassName,
  trigger,
  strokeWidth = 2.5,
}: ActionIconButtonProps) {
  // The label is a ReactNode (callers pass two stacked spans), so it cannot be
  // an aria-label string. The button points at the visible label instead, and
  // the label is hidden from the accessibility tree so it is read once, as the
  // button's name, including inside the Link wrapper.
  const labelId = useId();

  const buttonContent = (
    <motion.div
      whileHover={!disabled ? { scale: 1.05 } : undefined}
      whileTap={!disabled ? { scale: 0.95 } : undefined}
      className={cn(
        "flex flex-col items-center gap-2",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      )}
    >
      <div className="relative rounded-full">
        <Button
          variant={variant}
          size="icon"
          className={cn(
            "action-icon-btn w-10 h-10 lg:w-12 lg:h-12 rounded-full transition-all duration-300 relative overflow-hidden",
            className
          )}
          style={{
            boxShadow: [
              "0 2px 2px 0 oklch(from var(--brand-primary) calc(l * 0.5) c h / 0.35)",
              "0 1px 1px -1px oklch(from var(--brand-primary) calc(l * 0.5) c h / 0.2)",
            ].join(", "),
          }}
          aria-labelledby={labelId}
          disabled={disabled}
          onClick={onClick}
        >
          {/* Shine effect */}
          {}
          {!disabled && (
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent rounded-full"
              initial={{ x: "-100%" }}
              whileHover={{ x: "100%" }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
            />
          )}
          <Icon
            strokeWidth={strokeWidth}
            className={cn(
              "h-5 w-5 md:h-6 md:w-6",
              iconClassName || "text-primary"
            )}
          />
        </Button>
      </div>
      <span
        id={labelId}
        aria-hidden="true"
        className={cn(
          "text-xs md:text-sm font-semibold flex flex-col items-center",
          labelClassName
        )}
      >
        {label}
      </span>
    </motion.div>
  );

  // If there's a custom trigger, use it
  if (trigger) {
    return <>{trigger}</>;
  }

  // If disabled and has tooltip, wrap in tooltip
  if (disabled && tooltipText) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>{buttonContent}</div>
        </TooltipTrigger>
        <TooltipContent className="font-semibold">{tooltipText}</TooltipContent>
      </Tooltip>
    );
  }

  // If has href and not disabled, wrap in Link
  if (href && !disabled) {
    return <AppLink href={href}>{buttonContent}</AppLink>;
  }

  // Otherwise just return the button content
  return buttonContent;
}
